import React from "react";
import PropTypes from "prop-types";
import { visionURL } from "../../config/environment";
import makeStyles from "@mui/styles/makeStyles";
import {
  Typography,
  Box,
  Button,
  Grid,
  Select,
  InputLabel,
  FormControl,
  MenuItem
} from "@mui/material";
import ProfilePicture from "../../components/ProfilePicture/ProfilePicture";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { useTranslation } from "react-i18next";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { formatDate } from "../../utils/datetime";

const useStyles = makeStyles(theme => ({
  formControl: {
    "& > div": {
      marginTop: theme.spacing(4)
    }
  },
  select: {
    marginTop: theme.spacing(2.75)
  },
  formsContainer: {
    color: theme.palette.text.secondary,
    "& > div": {
      marginBottom: theme.spacing(1.5)
    }
  },
  fieldValue: {
    wordBreak: "break-word"
  }
}));

const UneditableField = ({ fieldName, fieldValue, inputProps }) => {
  const classes = useStyles();
  return (
    <Box
      data-testid={
        inputProps && inputProps["data-testid"]
          ? inputProps["data-testid"]
          : null
      }
      display="flex"
      flexDirection="column"
      alignItems={{ xs: "center", sm: "flex-start" }}
    >
      <Typography variant="caption" color="textSecondary">
        {fieldName}
      </Typography>
      <Typography
        variant="subtitle1"
        color="textPrimary"
        className={classes.fieldValue}
      >
        {fieldValue}
      </Typography>
    </Box>
  );
};
export default function Profile(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    isAdmin,
    onGoBack,
    fullName,
    firstName,
    lastName,
    position,
    location,
    phone,
    email,
    hireDate,
    preferredLanguage,
    profilePicture,
    updating,
    onProfilePictureApply,
    onSave,
    onCancel
  } = props;

  const name =
    fullName || `${firstName ? firstName : ""} ${lastName ? lastName : ""}`;

  const language =
    preferredLanguage === "en"
      ? t("employeeProfile.english")
      : preferredLanguage === "es"
      ? t("employeeProfile.spanish")
      : null;

  const [preferredLang, setPreferredLang] = React.useState(preferredLanguage);

  function handlePreferredLanguageChange(event) {
    setPreferredLang(event.target.value);
  }

  const isCancelling = React.useRef(false);
  const handleCancel = () => {
    // restore original state
    setPreferredLang(preferredLanguage || "en");
    // callback
    onCancel();
  };

  function handleSave() {
    let payload = {
      settings: {
        preferred_language: preferredLang
      }
    };
    onSave(payload);
  }

  function handleLogout() {
    window.location = `${visionURL}/users/sign_out`;
  }

  const title = isAdmin ? (
    <Breadcrumb routes={[t("employeeProfile.employees"), name]} />
  ) : (
    <Typography color="textPrimary" variant="h4">
      {t("employeeProfile.myUserProfile")}
    </Typography>
  );

  return (
    <AdminPageLayout
      title={title}
      headerActions={
        !isAdmin && (
          <Button color="primary" onClick={handleLogout}>
            {t("employeeProfile.logout")}
          </Button>
        )
      }
      onGoBackClick={onGoBack}
    >
      <Box
        display="flex"
        mt={{ xs: 5, sm: 4 }}
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <Box mr={{ xs: 0, sm: 4, md: 8, lg: 10 }} mb={{ xs: 4, sm: 0 }}>
          <ProfilePicture
            firstName={firstName}
            lastName={lastName}
            imageSrc={profilePicture}
            onApply={onProfilePictureApply}
          />
        </Box>
        <Grid container spacing={3} className={classes.formsContainer}>
          <Grid item xs={12} sm={6}>
            <UneditableField
              fieldName={t("employeeProfile.name")}
              fieldValue={name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <UneditableField
              fieldName={t("employeeProfile.position")}
              fieldValue={position}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <UneditableField
              fieldName={t("employeeProfile.location")}
              fieldValue={location}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <UneditableField
              fieldName={t("employeeProfile.phone")}
              fieldValue={phone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <UneditableField
              fieldName={t("employeeProfile.email")}
              fieldValue={email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <UneditableField
              fieldName={t("employeeProfile.hireDate")}
              fieldValue={formatDate(hireDate)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {!isAdmin ? (
              <FormControl
                className={classes.formControl}
                variant="filled"
                fullWidth
              >
                <InputLabel>
                  {t("employeeProfile.preferredLanguage")}
                </InputLabel>
                <Select
                  variant="filled"
                  className={classes.select}
                  disableUnderline
                  value={preferredLang}
                  onChange={handlePreferredLanguageChange}
                  hiddenLabel
                >
                  <MenuItem value={""} disabled>
                    {t("employeeProfile.selectPreferredLanguage")}
                  </MenuItem>
                  <MenuItem value={"en"} key={"en"}>
                    {t("employeeProfile.english")}
                  </MenuItem>
                  <MenuItem value={"es"} key={"es"}>
                    {t("employeeProfile.spanish")}
                  </MenuItem>
                </Select>
              </FormControl>
            ) : (
              <UneditableField
                inputProps={{
                  "data-testid": "preferred-language-uneditable-field"
                }}
                fieldName={t("employeeProfile.preferredLanguage")}
                fieldValue={language || t("employeeProfile.english")}
              />
            )}
          </Grid>
          {!isAdmin && (
            <Grid item xs={12}>
              <Box
                display="flex"
                flex={1}
                justifyContent="flex-end"
                pr={1.5}
                mt={7}
              >
                <Box mr={3}>
                  <Button
                    color="primary"
                    onClick={handleCancel}
                    // beat the onBlur event https://stackoverflow.com/a/15196689
                    onMouseDown={() => (isCancelling.current = true)}
                    onMouseUp={() => (isCancelling.current = false)}
                  >
                    {t("employeeProfile.cancel")}
                  </Button>
                </Box>
                <Button
                  color="primary"
                  onClick={handleSave}
                  disabled={updating}
                  variant="contained"
                >
                  {t("employeeProfile.save")}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </AdminPageLayout>
  );
}

Profile.propTypes = {
  isAdmin: PropTypes.bool,
  fullName: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  position: PropTypes.string,
  location: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  hireDate: PropTypes.string,
  preferredLanguage: PropTypes.string,
  profilePicture: PropTypes.any,
  profilePictureContentType: PropTypes.any,
  updating: PropTypes.bool,
  onProfilePictureApply: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onGoBack: PropTypes.func
};
